import { STALE_TIMES } from '@iheartjane/react-query-utils';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { BrandResponse } from '@jane/business-admin/types';
import { zBrandResponse } from '@jane/business-admin/types';
import { isTransientError } from '@jane/business-admin/util';
import { janeApiV2, safeParseAsync } from '@jane/shared/data-access';

import { STORES_URL } from './stores';

type fetchBrandsParams = {
  id?: string;
  names?: string;
  order?: string;
  page?: number;
  page_offset?: number;
  per_page?: number;
};

const fetchBrands = async (
  storeId: string,
  params: fetchBrandsParams
): Promise<BrandResponse> => {
  const urlParams = new URLSearchParams();
  params.names && urlParams.append('names', params.names);
  params.id && urlParams.append('id', params.id);
  params.order && urlParams.append('order', params.order);
  params.page && urlParams.append('page', params.page.toString());
  params.per_page && urlParams.append('per_page', params.per_page.toString());
  params.page_offset &&
    urlParams.append('page_offset', params.page_offset.toString());

  const url = `${STORES_URL}/${storeId}/store_brands?${urlParams.toString()}`;
  const response = await janeApiV2.get<BrandResponse>(url);

  return await safeParseAsync(zBrandResponse, response, 'fetchBrands');
};

const fetchBrandsQueryKey = (storeId: string, params: fetchBrandsParams) => {
  return ['storeBrands', storeId, params];
};

export const useFetchBrands = (
  storeId: string,
  params: fetchBrandsParams,
  enabled: boolean
) => {
  const brandsQuery = useInfiniteQuery<BrandResponse>({
    queryFn: async ({ pageParam = 1 }) => {
      const data = await fetchBrands(storeId, {
        order: params.order,
        per_page: params.per_page,
        page: pageParam,
        page_offset: params.page_offset,
        names: params.names,
        id: params.id,
      });
      return {
        ...data,
        pageParam,
      };
    },
    queryKey: fetchBrandsQueryKey(storeId, params),
    getNextPageParam: (lastPage) => {
      const hasNextPage = lastPage?.meta.page < lastPage.meta.number_of_pages;
      return hasNextPage ? lastPage.meta.page + 1 : undefined;
    },
    enabled,
    staleTime: STALE_TIMES.short,
    useErrorBoundary: (error) => !isTransientError(error),
  });

  let brands = undefined;
  if (brandsQuery.data) {
    const { pages } = brandsQuery.data;
    brands = pages.flatMap((body) => body.brands);
  }

  return { ...brandsQuery, data: brands };
};

type fetchGlobalBrandsParams = {
  ids?: string;
  names?: string;
  order?: string;
  page?: number;
  page_offset?: number;
  per_page?: number;
  retail_org_id?: number;
};

const fetchGlobalBrands = async (
  params: fetchGlobalBrandsParams
): Promise<BrandResponse> => {
  const urlParams = new URLSearchParams();
  params.names && urlParams.append('filters[names]', params.names);
  params.ids && urlParams.append('filters[ids]', params.ids);
  params.order && urlParams.append('order', params.order);
  params.page && urlParams.append('page', params.page.toString());
  params.per_page && urlParams.append('per_page', params.per_page.toString());
  params.page_offset &&
    urlParams.append('page_offset', params.page_offset.toString());
  params.retail_org_id &&
    urlParams.append('organization_id', params.retail_org_id.toString());

  const url = `/business/specials/brands?${urlParams.toString()}`;
  const response = await janeApiV2.get<BrandResponse>(url);

  return await safeParseAsync(zBrandResponse, response, 'fetchGlobalBrands');
};

const fetchGlobalBrandsQueryKey = (params: fetchGlobalBrandsParams) => {
  return ['globalBrands', params];
};

const fetchTargetedBrands = async (ids: string): Promise<BrandResponse> => {
  const urlParams = new URLSearchParams();
  urlParams.append('ids', ids);

  const url = `/business/specials/brands/targeted?${urlParams.toString()}`;
  const response = await janeApiV2.get<BrandResponse>(url);

  return await safeParseAsync(zBrandResponse, response, 'fetchTargetedBrands');
};

export const useFetchGlobalBrands = (
  params: fetchGlobalBrandsParams,
  enabled: boolean
) => {
  const brandsQuery = useInfiniteQuery<BrandResponse>({
    queryFn: async ({ pageParam = 1 }) => {
      const data = await fetchGlobalBrands({
        ...params,
        page: pageParam,
      });
      return {
        ...data,
        pageParam,
      };
    },
    queryKey: fetchGlobalBrandsQueryKey(params),
    getNextPageParam: (lastPage) => {
      const hasNextPage = lastPage?.meta.page < lastPage.meta.number_of_pages;
      return hasNextPage ? lastPage.meta.page + 1 : undefined;
    },
    enabled,
    staleTime: Infinity,
    useErrorBoundary: (error) => !isTransientError(error),
  });

  let brands = undefined;
  if (brandsQuery.data) {
    const { pages } = brandsQuery.data;
    brands = pages.flatMap((body) => body.brands);
  }

  return { ...brandsQuery, data: brands };
};

export const useFetchTargetedBrands = (
  ids: string,
  requiredOrDiscounted: string,
  includesOrExcludes: string
) => {
  const brandsQuery = useInfiniteQuery<BrandResponse>({
    queryFn: async ({ pageParam = 1 }) => {
      const data = await fetchTargetedBrands(ids || ('' as string));
      return {
        ...data,
        pageParam,
      };
    },
    queryKey: [
      `targetedBrands-${requiredOrDiscounted}-${includesOrExcludes}-${ids}`,
    ],
    getNextPageParam: (lastPage) => {
      const hasNextPage = lastPage?.meta.page < lastPage.meta.number_of_pages;
      return hasNextPage ? lastPage.meta.page + 1 : undefined;
    },
    enabled: true,
    staleTime: Infinity,
    useErrorBoundary: (error) => !isTransientError(error),
  });

  let brands = undefined;
  if (brandsQuery.data) {
    const { pages } = brandsQuery.data;
    brands = pages.flatMap((body) => body.brands);
  }

  return { ...brandsQuery, data: brands };
};
